"use client"

import { useLocale, useTranslations } from "next-intl"
import { useRef, useState } from "react"
import { Button } from "@nextui-org/react"
import { useCookies } from "next-client-cookies"
import { trackFunnel } from "@/lib/funnel"
import axios from "axios"
import Image from "next/image"
import LogoImg from "@/public/images/logovexub.png"

// Toggle: true = modal, false = redirect to /signup
export const SIGNUP_MODAL_ENABLED = false

export default function SignupModal({ isOpen, onClose, onSwitchToLogin }: { isOpen: boolean; onClose: () => void; onSwitchToLogin?: () => void }) {
    const t = useTranslations("Auth")
    const local = useLocale()
    const cookies = useCookies()

    const [errorMessage, setErrorMessage] = useState("")
    const [etapeSignup, setEtapeSignup] = useState(0)
    const [email, setEmail] = useState("")
    const [password, setPassword] = useState("")
    const [confirmPassword, setConfirmPassword] = useState("")
    const [isVerifyingCode, setIsVerifyingCode] = useState(false)
    const [otp, setOtp] = useState(Array(6).fill(""))
    const [allOtpIsAvailable, setAllOtpIsAvailable] = useState(false)
    const inputsRef = useRef<Array<HTMLInputElement>>([])
    const isApple = typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.userAgent)

    if (!isOpen) return null

    const reset = () => {
        setEtapeSignup(0)
        setErrorMessage("")
        setEmail("")
        setPassword("")
        setConfirmPassword("")
        setOtp(Array(6).fill(""))
    }

    const GoogleSignIn = async () => {
        const { data } = await axios.get("https://vexub.fr/api/vexub_v2/compte/google_geturl.php")
        window.location.href = data.url
    }

    function setCookie(name: string, value: string, days: number) {
        const d = new Date()
        d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000)
        document.cookie = `${name}=${value};expires=${d.toUTCString()};path=/;Secure;SameSite=Strict`
    }

    const completeCode = (fullCode: string) => {
        setIsVerifyingCode(true)
        verifyOTP(fullCode)
    }

    const verifyOTP = async (code: string) => {
        setErrorMessage("")
        const postData = new URLSearchParams()
        postData.append("email", email)
        postData.append("secretcode", code)
        try {
            const response = await axios.post("https://vexub.fr/api/vexub_v2/compte/login_2.php", postData)
            if (response.data.status === "success") {
                let nbComptes = parseInt(localStorage.getItem("nb_comptes") || "0", 10)
                if (nbComptes >= 3) { setErrorMessage(t("TooMuchAccount")); setIsVerifyingCode(false); return }
                nbComptes++
                localStorage.setItem("nb_comptes", `${nbComptes}`)
                setCookie("token", response.data.token, 7)
                setCookie("email", email, 7)
                trackFunnel("signup_complete", { email })
                window.location.href = "/app"
            } else {
                setErrorMessage(t("ErrorCode"))
                setIsVerifyingCode(false)
            }
        } catch (error) {
            console.error(error)
            setIsVerifyingCode(false)
        }
    }

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault()
        const pastedData = e.clipboardData.getData("Text").replace(/\D/g, "").slice(0, 6)
        if (!pastedData.length) return
        const newOtp = [...otp]
        for (let i = 0; i < 6; i++) { newOtp[i] = pastedData[i] || ""; if (inputsRef.current[i]) inputsRef.current[i]!.value = pastedData[i] || "" }
        setOtp(newOtp)
        inputsRef.current[Math.min(pastedData.length, 5)]?.focus()
        if (pastedData.length === 6) completeCode(pastedData)
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        setErrorMessage("")
        const value = e.target.value.replace(/\D/g, "")
        if (!value) return
        const updatedOtp = [...otp]
        updatedOtp[index] = value[0]
        setOtp(updatedOtp)
        if (index < 5) inputsRef.current[index + 1]?.focus()
        if (updatedOtp.every((d) => d !== "")) { setAllOtpIsAvailable(true); completeCode(updatedOtp.join("")) }
        else setAllOtpIsAvailable(false)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === "Backspace") { e.currentTarget.value = ""; if (index > 0) inputsRef.current[index - 1]?.focus() }
        const updatedOtp = [...otp]; updatedOtp[index] = ""; setOtp(updatedOtp)
        setAllOtpIsAvailable(updatedOtp.every((d) => d !== ""))
    }

    function encryptToMD5(input: string): string {
        const h = "0123456789abcdef"; let r = ""
        for (let i = 0; i < input.length; i++) { const c = input.charCodeAt(i); r += h.charAt((c >> 4) & 0x0f) + h.charAt(c & 0x0f) }
        return r
    }

    const inscription = async () => {
        setErrorMessage("")
        if (password.length < 8) { setErrorMessage(t("ErrorPasswordSignup")); return }
        if (password !== confirmPassword) { setErrorMessage(t("errorConfirmMdp")); return }
        const postData = new URLSearchParams()
        postData.append("email", email)
        postData.append("mdp", encryptToMD5(password))
        postData.append("pseudo", "Vexub_COM")
        postData.append("affiliation_tag", localStorage.getItem("affiliation_tag") || "")
        postData.append("langsite", local)
        postData.append("firsttimeurl", localStorage.getItem("firsttimeurl") || "RIEN")
        postData.append("country", cookies.get("country") || "")
        try {
            const response = await axios.post("https://vexub.fr/api/vexub_v2/compte/inscription2.php", postData)
            if (response.data.status === "success") { setEtapeSignup(1); trackFunnel("signup_start", { email }) }
            else if (response.data.msg === "err_compte_existe_deja") setErrorMessage(t("ErrorEmailAlreadyUsed"))
        } catch (error) { console.error(error) }
    }

    const handleBackdropClick = (e: React.MouseEvent) => { if (e.target === e.currentTarget) { onClose(); reset() } }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={handleBackdropClick}>
            <div className="relative w-full max-w-sm mx-4 bg-white rounded-xl p-6 shadow-2xl">
                {/* Close */}
                <button onClick={() => { onClose(); reset() }} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>

                {/* Logo */}
                <div className="flex justify-center mb-4">
                    <Image src={LogoImg} width={38} height={38} alt="Vexub" priority />
                </div>

                <h2 className="text-xl font-bold text-center text-gray-900 mb-6">{t("createTitre")}</h2>

                {/* Step 0: Sign up form */}
                <div style={{ display: etapeSignup === 0 ? "" : "none" }} className="flex flex-col gap-3">
                    <input value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded border border-gray-300 px-3 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent placeholder-gray-400" type="email" placeholder={`${t("email")} *`} required />

                    <input value={password} onChange={(e) => setPassword(e.target.value)} className="w-full rounded border border-gray-300 px-3 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent placeholder-gray-400" type="password" placeholder={`${t("mdp")} *`} autoComplete="on" required />

                    <div>
                        <input value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="w-full rounded border border-gray-300 px-3 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent placeholder-gray-400" type="password" placeholder={`${t("confirmMdp")} *`} autoComplete="on" required />
                        <p className="text-xs text-gray-400 mt-1">{t("Password must have 8 characters")}</p>
                    </div>

                    <button onClick={inscription} className="w-full bg-purple-500 hover:bg-purple-600 text-white font-bold py-2.5 rounded-full transition uppercase tracking-wider text-sm">
                        {t("signupButton")}
                    </button>

                    {errorMessage && <p className="text-red-500 text-center text-sm">{errorMessage}</p>}

                    <div className="flex items-center">
                        <div className="border-t border-gray-300 grow mr-3" />
                        <span className="text-base text-gray-500 font-medium">{t("ou")}</span>
                        <div className="border-t border-gray-300 grow ml-3" />
                    </div>

                    <button onClick={GoogleSignIn} className="inline-flex items-center justify-center rounded-full border border-gray-300 text-sm font-semibold h-10 w-full bg-white text-gray-700 hover:bg-gray-50 transition">
                        <span className="mr-2 w-5">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 186.69 190.5">
                                <path fill="#4285f4" d="M95.25 77.932v36.888h51.262c-2.251 11.863-9.006 21.908-19.137 28.662l30.913 23.986c18.011-16.625 28.402-41.044 28.402-70.052 0-6.754-.606-13.249-1.732-19.483z"/>
                                <path fill="#34a853" d="m41.869 113.38-6.972 5.337-24.679 19.223c15.673 31.086 47.796 52.561 85.03 52.561 25.717 0 47.278-8.486 63.038-23.033l-30.913-23.986c-8.486 5.715-19.31 9.179-32.125 9.179-24.765 0-45.806-16.712-53.34-39.226z"/>
                                <path fill="#fbbc05" d="M10.218 52.561C3.724 65.376.001 79.837.001 95.25s3.723 29.874 10.217 42.689c0 .086 31.693-24.592 31.693-24.592-1.905-5.715-3.031-11.776-3.031-18.098s1.126-12.383 3.031-18.098z"/>
                                <path fill="#ea4335" d="M95.25 37.927c14.028 0 26.497 4.849 36.455 14.201l27.276-27.276C142.442 9.439 120.968 0 95.25 0 58.016 0 25.891 21.388 10.218 52.561L41.91 77.153c7.533-22.514 28.575-39.226 53.34-39.226"/>
                            </svg>
                        </span>
                        {t("continuerGoogle")}
                    </button>

                    {isApple && (
                        <button onClick={() => window.location.href = "/signup"} className="inline-flex items-center justify-center rounded-full border border-gray-300 text-sm font-semibold h-10 w-full bg-white text-gray-700 hover:bg-gray-50 transition">
                            <span className="mr-2 w-5">
                                <svg viewBox="0 0 22.773 22.773" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M15.769,0c0.053,0,0.106,0,0.162,0c0.13,1.606-0.483,2.806-1.228,3.675c-0.731,0.863-1.732,1.7-3.351,1.573c-0.108-1.583,0.506-2.694,1.25-3.561C13.292,0.879,14.557,0.16,15.769,0z"/>
                                    <path d="M20.67,16.716c0,0.016,0,0.03,0,0.045c-0.455,1.378-1.104,2.559-1.896,3.655c-0.723,0.995-1.609,2.334-3.191,2.334c-1.367,0-2.275-0.879-3.676-0.903c-1.482-0.024-2.297,0.735-3.652,0.926c-0.155,0-0.31,0-0.462,0c-0.995-0.144-1.798-0.932-2.383-1.642c-1.725-2.098-3.058-4.808-3.306-8.276c0-0.34,0-0.679,0-1.019c0.105-2.482,1.311-4.5,2.914-5.478c0.846-0.52,2.009-0.963,3.304-0.765c0.555,0.086,1.122,0.276,1.619,0.464c0.471,0.181,1.06,0.502,1.618,0.485c0.378-0.011,0.754-0.208,1.135-0.347c1.116-0.403,2.21-0.865,3.652-0.648c1.733,0.262,2.963,1.032,3.723,2.22c-1.466,0.933-2.625,2.339-2.427,4.74C17.818,14.688,19.086,15.964,20.67,16.716z"/>
                                </svg>
                            </span>
                            {t("continuerApple")}
                        </button>
                    )}

                    <p className="text-center text-sm text-gray-500 mt-1">
                        {t("dejaCompte")} {onSwitchToLogin ? (
                            <button onClick={() => { onClose(); reset(); onSwitchToLogin() }} className="font-medium text-purple-600 hover:text-purple-500 transition underline">{t("loginButton")}</button>
                        ) : (
                            <a href="/signin" className="font-medium text-purple-600 hover:text-purple-500 transition underline">{t("loginButton")}</a>
                        )}
                    </p>
                </div>

                {/* Step 1: OTP */}
                <div style={{ display: etapeSignup === 1 ? "" : "none" }} className="flex flex-col gap-4">
                    <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg p-3">
                        <svg className="w-6 h-6 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        <p className="text-sm text-gray-700">{t("OTPDesc1")}</p>
                    </div>
                    <input
                        inputMode="numeric"
                        type="text"
                        maxLength={6}
                        disabled={isVerifyingCode}
                        placeholder="Code *"
                        className="w-full rounded border border-gray-300 px-3 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent placeholder-gray-400"
                        onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, "").slice(0, 6)
                            e.target.value = val
                            if (val.length === 6) completeCode(val)
                        }}
                    />
                    {errorMessage && <p className="text-red-500 text-center text-sm">{errorMessage}</p>}
                    <button disabled={isVerifyingCode} onClick={() => { const input = document.querySelector<HTMLInputElement>('input[maxlength="6"]'); if (input && input.value.length === 6) completeCode(input.value) }} className="w-full bg-purple-500 hover:bg-purple-600 disabled:opacity-50 text-white font-bold py-2.5 rounded-full transition uppercase tracking-wider text-sm">
                        {isVerifyingCode ? "..." : t("Valider")}
                    </button>
                </div>
            </div>
        </div>
    )
}
